import { useEffect, useRef, useState } from "react";
import { Phone } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { upsertUserAttributes } from "@/lib/userAttributes";
import { trackEvent } from "@/lib/analytics";

const DISMISS_KEY = "ha_phone_prompt_dismissed_at";
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
const SMS_CONSENT_VERSION = "2026-09-signup-v1";

function formatPhone(raw: string) {
  let d = raw.replace(/\D/g, "");
  if (d.length > 10 && d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

interface Props {
  placement: "dashboard" | "stack_results";
  className?: string;
}

export default function PhoneCapturePrompt({ placement, className = "" }: Props) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) { setVisible(false); return; }
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (at && Date.now() - at < DISMISS_MS) return;
    } catch { /* ignore */ }
    (async () => {
      const { data } = await supabase.from("user_attributes").select("phone").eq("user_id", user.id).maybeSingle();
      if (cancelled) return;
      const has = Boolean(String((data as any)?.phone ?? "").trim());
      setVisible(!has);
      if (!has && !shownRef.current) {
        shownRef.current = true;
        trackEvent("phone_prompt_shown", { placement });
      }
    })();
    return () => { cancelled = true; };
  }, [user, placement]);

  if (!user || (!visible && !saved)) return null;

  if (saved) {
    return (
      <Card className={`mb-6 p-4 text-sm text-foreground ${className}`}>
        Thanks! We'll use that number when your next step is ready.
      </Card>
    );
  }

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) { setError("Please enter a 10-digit U.S. mobile number."); return; }
    setSaving(true);
    setError(null);
    await upsertUserAttributes({ phone: digits });
    supabase.rpc("record_sms_consent", { p_version: SMS_CONSENT_VERSION }).then(() => {}, () => {});
    trackEvent("phone_prompt_saved", { placement });
    setSaving(false);
    setSaved(true);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    trackEvent("phone_prompt_dismissed", { placement });
    setVisible(false);
  };

  return (
    <Card className={`mb-6 p-4 sm:p-5 ${className}`}>
      <form onSubmit={save} className="space-y-3">
        <div className="flex items-start gap-3">
          <Phone className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <p className="font-semibold text-foreground">Want a call or text when your next step is ready?</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <label htmlFor={`phone-prompt-${placement}`} className="sr-only">Mobile number</label>
          <Input
            id={`phone-prompt-${placement}`}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => { setPhone(formatPhone(e.target.value)); setError(null); }}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `phone-prompt-err-${placement}` : undefined}
          />
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </div>
        {error && <p id={`phone-prompt-err-${placement}`} className="text-sm text-destructive">{error}</p>}
        <p className="text-xs text-muted-foreground">
          By tapping "Save," I agree to receive calls and texts from Home Approach at this number, including automated messages. Consent is not required to use Home Approach. Message &amp; data rates may apply; I can opt out anytime.
        </p>
        <button type="button" onClick={dismiss} className="text-xs text-muted-foreground underline">Not now</button>
      </form>
    </Card>
  );
}
